import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTouchProject } from '../../features/shell/projectMru';
import { useMutation, useQuery, useQueryClient, type Query } from '@tanstack/react-query';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { Search as SearchIcon, Archive as ArchiveIcon, FileText as DraftsIcon, Settings as SettingsIcon, Pencil, SlidersHorizontal, Keyboard } from 'lucide-react';
import { buildDescriptionPreview, type TicketSummary, type TicketTypeRef, type User, type ColumnId, type ProjectViewPrefs, type LiveEvent } from '@vd/types';
import { Avatar, Badge, Button, Input, Menu, MenuItem, MenuDivider, Modal, ModalBody, ModalFooter, ModalHeader, Popover, Tooltip, useToast, cn } from '@vd/ui';
import { bigSendCheck, jiraWatermark, sendInBatches, sentToJiraSummary, unlinkedSummary } from '../../features/jira/sendSummary';
import { JiraMenuItem } from '../../features/jira/JiraMenuItem';
import { UrlPatternHelp } from '../../features/projects/UrlPatternHelp';
import { boardTicketListsKey, isTypeFilteredList, projectTicketSearchKey, useBoardData } from '../../features/board/useBoardData';
import { useBoardPrefs } from '../../features/board/boardPrefs';
import { BoardToolbar } from '../../features/board/BoardToolbar';
import { BoardView } from '../../features/board/BoardView';
import { placeAtAnchor, restorePlace, type DropAnchor } from '../../features/board/dropAnchor';
import { BulkActionBar } from '../../features/board/BulkActionBar';
import { useInertUnderBar } from '../../features/board/useInertUnderBar';
import { CardsView } from '../../features/board/CardsView';
import { SingleView } from '../../features/board/SingleView';
import { TableView } from '../../features/board/TableView';
import { ViewToggle } from '../../features/board/ViewToggle';
import { ColumnDot } from '../../features/board/ColumnDot';
import { AssigneePicker } from '../../features/ticket/AssigneePicker';
import { MoveToProjectModal } from '../../features/modals/MoveToProjectModal';
import { activeFilterNames, clearedFilterPrefs } from '../../features/board/activeFilters';
import { moveTickets, refreshTicketLists, staleBoards } from '../../features/board/bulkMove';
import { DestructiveConfirm } from '../../features/modals/DestructiveConfirm';
import { BranchNameModal } from '../../features/ticket/BranchNameModal';
import { CreateCaptureModal } from '../../features/modals/CreateCaptureModal';
import { AdvancedSearchModal } from '../../features/modals/AdvancedSearchModal';
import { ShortcutsCheatsheet } from '../../features/modals/ShortcutsCheatsheet';
import { useShortcutMap } from '../../lib/keyboard';
import { copyToClipboard } from '../../lib/clipboard';
import { safeOpen } from '../../lib/safeOpen';
import { api } from '../../lib/api';
import { useAuth } from '../../features/auth/AuthProvider';
import { InlineProjectName } from '../../features/projects/InlineProjectName';
import { BULK_REQUEST_LIMIT, settleWithLimit } from '../../lib/settleWithLimit';
import { EVERY_SCREEN, PROJECT_BOARD_SCREEN } from '../../lib/screenNames';
import styles from './ProjectViewsPage.module.css';
import { ProjectMark } from '../../features/projects/ProjectMark';
import { readIconSoon } from '../../features/projects/iconLook';
import { agentAfterComment, agentAfterMove } from '../../features/board/agentWorkingLive';
import { useTabProject, useTabTitle, useTabWorkspaceName } from '../../lib/tabTitle';
import { displayTimeZone, wallTimeEndIn, wallTimeIn } from '../../lib/time';

/**
 * The page a ticket was captured on, when it is somewhere safe to send
 * somebody.
 *
 * A ticket's source URL is whatever the page said it was at capture time, and
 * this opens it in a new tab - so the scheme is checked rather than trusted.
 * `javascript:` and `data:` are the two that turn a menu item into script
 * execution or a fabricated page, and neither is a page anybody captured.
 */
export function capturedPageUrl(sourceUrl: string | undefined): string | null {
  if (!sourceUrl) return null;
  try {
    const parsed = new URL(sourceUrl);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null;
  } catch {
    return null;
  }
}

/** One empty list, so "no tickets" is the same list on every render. */
const NO_TICKETS: TicketSummary[] = [];

/**
 * `tickets` with one ticket's type replaced, the field left out when it has
 * none, as a summary says that. The same list when the ticket is not in it.
 */
function withType(tickets: TicketSummary[], ticketId: string, type: TicketTypeRef | undefined): TicketSummary[] {
  if (!tickets.some((t) => t.id === ticketId)) return tickets;
  return tickets.map((t) => {
    if (t.id !== ticketId) return t;
    const next: TicketSummary = { ...t };
    if (type) next.type = type;
    else delete next.type;
    return next;
  });
}

export function ProjectViewsPage() {
  // This tab's title and icon: the project's mark (TOADO-905).
  const tabWorkspace = useTabWorkspaceName();
  const tabProject = useTabProject(useParams<{ projectId: string }>().projectId);
  useTabTitle({ parts: [tabProject?.name, tabWorkspace], project: tabProject });
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // A card opens over the board, which stays where it was (Jason, 18 Oct;
  // TicketModal). The address is the ticket's own, so it is still a link.
  const openTicketOverBoard = (ticketId: string) => navigate(`/tickets/${ticketId}`, { state: { backgroundLocation: location } });
  const qc = useQueryClient();
  const toast = useToast();

  /**
   * The type filter is read from prefs HERE, ahead of the data hook, because it
   * is applied server-side (TOADO-411) - it is an input to the tickets query
   * rather than something applied to its result the way every other filter on
   * this page is. `useBoardPrefs` is a store selector, so reading it twice on one
   * render costs nothing, and the narrow selector means a change to any other
   * pref does not re-run the query.
   */
  const typeFilter = useBoardPrefs((s) => (projectId ? s.get(projectId)?.typeIds : undefined));
  const { project, columns, tickets, members, usersById, ticketTypes } =
    useBoardData(projectId, typeFilter);
  const { user, companies, currentCompanyId } = useAuth();
  const currentCompany = companies.find((c) => c.id === currentCompanyId);
  const touchProject = useTouchProject();

  // Promote this project in the sidebar's MRU whenever we land here — covers
  // direct URL loads, card clicks, inbox navigation, etc. (The sidebar and
  // All Projects page also touch on click, which keeps the ordering snappy
  // without waiting for the board to mount.)
  useEffect(() => {
    if (projectId) touchProject(projectId);
  }, [projectId, touchProject]);

  // Live updates from the /live WebSocket. Previously this subscriber
  // called `invalidateQueries(['tickets', projectId])` on every event,
  // which triggered a full re-fetch of the board's ticket list per event.
  // Under rapid capture work (each capture → ticket.created → AI title
  // arrives → ticket.updated → MCP comment → comment.created etc.) that
  // produced 3-4 stacked refetches in seconds, exhausted the pg pool, and
  // made individual /tickets calls balloon from 1.5s baseline to 12-15s.
  // See docs/deployment-DO/TICKETS_LIST_PERF.md for the measured cascade.
  //
  // Now: surgical setQueryData per event type — update the cached
  // TicketSummary array directly from the event payload, no network
  // refetch needed for most events. The only events that still need a
  // single-ticket GET are `ticket.created` (need the new ticket's
  // summary fields) and `ticket.restored` (need to recover the summary).
  // Single-ticket GET is much cheaper than the full list refetch.
  //
  // Every move on this board, for telling whether one is still in flight.
  const moveKey = ['board-move', projectId] as const;
  // Tickets a live event deleted or archived while a move of theirs was in
  // flight. A refused move to another project puts its ticket back on this
  // board, which must not bring back one of these.
  const removedDuringMove = useRef(new Set<string>());

  // Cache shape: every board list of the project (one per type filter,
  // under `boardTicketListsKey`) stores `TicketSummary[]` directly
  // (useBoardData unwraps `r.items`), and each event is written to all
  // of them: a filter switched back to within the stale window shows the
  // same board as the one left. Cross-project events (other projects in
  // the same company) get ignored cheaply.
  useEffect(() => {
    if (!projectId) return;
    const lists = { queryKey: boardTicketListsKey(projectId) };
    // The newest Jira word applied per ticket, for as long as this board listens.
    const acceptJira = jiraWatermark();
    // Whether a new ticket belongs in a type-filtered list is the server's
    // call, so those are refetched rather than appended to.
    const unfilteredLists = { ...lists, predicate: (q: Query) => !isTypeFilteredList(q.queryKey) };
    const filteredLists = { ...lists, predicate: (q: Query) => isTypeFilteredList(q.queryKey) };
    const noteRemoved = (ticketId: string) => {
      const moving = qc.isMutating({
        mutationKey: ['board-move', projectId],
        predicate: (m) => (m.state.variables as { ticketId?: string } | undefined)?.ticketId === ticketId,
      });
      if (moving > 0) removedDuringMove.current.add(ticketId);
    };

    return api.subscribe((e: LiveEvent) => {
      // ticket.created carries projectId — skip cross-project noise.
      if (e.type === 'ticket.created' && e.projectId !== projectId) return;

      switch (e.type) {
        case 'ticket.created': {
          // Event payload doesn't include the summary, so fetch it: this
          // project's list, asked for this one ticket. Not the full ticket,
          // which lacks what only the list route adds (the Session and video
          // marks, t-UI8InD), and not the whole list, which is what the live
          // updates exist to avoid.
          //
          // An empty answer means the ticket is not on this board (moved
          // between the event and our GET, or a draft), so nothing is added.
          //
          // On error, fall back to a list invalidate so the user
          // eventually sees the new ticket (same behavior as the
          // pre-fix code path).
          //
          // Appended at the end of its column, where the server puts a new
          // ticket. A published draft is the exception: it keeps the place it
          // was given at capture, so it can belong above tickets created since,
          // and shows last here until the list is next refetched (TOADO-514).
          api.listTicketSummaries(projectId, [e.ticketId]).then(({ items }) => {
            const ticket = items.find((t) => t.id === e.ticketId);
            if (!ticket) return;
            qc.setQueriesData<TicketSummary[]>(unfilteredLists, (old) => {
              if (!old) return old;
              if (old.some((t) => t.id === ticket.id)) return old; // dedupe
              return [...old, ticket];
            });
            qc.invalidateQueries(filteredLists);
          }).catch(() => {
            qc.invalidateQueries(lists);
          });
          return;
        }

        case 'ticket.updated': {
          // Merge the changed fields into the matching summary in
          // place. `descriptionPreview` is server-computed from the
          // first 240 chars of description — recompute client-side
          // when description changes so the board card preview stays
          // in sync without a refetch.
          //
          // The `some()` guard before `.map()` matters: most events
          // don't carry projectId, so they fire for every project in
          // the company. Without the guard, every cross-project event
          // would allocate a new array (same content) and trigger a
          // board re-render via React Query's reference-equality
          // check. With the guard, cross-project events are an O(n)
          // scan and zero allocations.
          //
          // A type arrives as null when cleared, which a summary says by
          // leaving the field out. And a retype changes which type-filtered
          // lists the ticket belongs in, so those are refetched: patching one
          // in place would keep a ticket that no longer matches its filter.
          const { type, jira, jiraRevision, ...fields } = e.fields;
          const retyped = type !== undefined;
          qc.setQueriesData<TicketSummary[]>(retyped ? unfilteredLists : lists, (old) => {
            if (!old || !old.some((t) => t.id === e.ticketId)) return old;
            return old.map((t) => {
              if (t.id !== e.ticketId) return t;
              const merged: TicketSummary = { ...t, ...fields };
              if (type) merged.type = type;
              else if (type === null) delete merged.type;
              // Where it stands with Jira, said as the type is: null for none,
              // and never an older word over a newer one, a removal included
              // (TOADO-708).
              if (jira !== undefined && acceptJira(t.id, t.jira, jira, jiraRevision)) {
                if (jira) merged.jira = jira;
                else delete merged.jira;
              }
              // Shared with the server-side toSummary in
              // apps/api/src/routes/_serializers.ts so the formula
              // can't drift between the two call sites.
              if (fields.description !== undefined) {
                merged.descriptionPreview = buildDescriptionPreview(fields.description);
              }
              return merged;
            });
          });
          if (retyped) qc.invalidateQueries(filteredLists);
          return;
        }

        case 'ticket.moved': {
          // An agent's move starts the card's shimmer, anyone else's ends it (t-gdi2Cd).
          const agentWorking = agentAfterMove(e, new Date().toISOString());
          qc.setQueriesData<TicketSummary[]>(lists, (old) => {
            if (!old || !old.some((t) => t.id === e.ticketId)) return old;
            return old.map((t) =>
              t.id === e.ticketId ? { ...t, columnId: e.toColumnId, agentWorking } : t,
            );
          });
          return;
        }

        case 'ticket.assigned': {
          qc.setQueriesData<TicketSummary[]>(lists, (old) => {
            if (!old || !old.some((t) => t.id === e.ticketId)) return old;
            return old.map((t) =>
              t.id === e.ticketId ? { ...t, assignees: e.assignees } : t,
            );
          });
          return;
        }

        case 'ticket.archived': {
          // Drop from the active list. The board's list query filters
          // archived rows server-side, so the cached array shouldn't
          // contain them. Also invalidate the archive page's separate
          // cache so it reflects the new arrival if the user has it
          // open in another tab.
          //
          // Cross-project guard via `some()` — same rationale as
          // ticket.updated above. If the ticket isn't in this view's
          // active cache, it's another project's ticket — skip both
          // the cache rewrite AND the archive-cache invalidate (the
          // other project's archive page, not ours, would need that
          // refetch).
          noteRemoved(e.ticketId);
          const held = qc.getQueriesData<TicketSummary[]>(lists)
            .some(([, list]) => list?.some((t) => t.id === e.ticketId));
          if (!held) return;
          qc.setQueriesData<TicketSummary[]>(lists, (old) =>
            old ? old.filter((t) => t.id !== e.ticketId) : old,
          );
          qc.invalidateQueries({ queryKey: ['tickets', projectId, 'archived'] });
          return;
        }

        case 'ticket.restored': {
          // Refetched, not appended as a new ticket is: a restored ticket
          // keeps the place in its column it had before it was archived,
          // which the event does not say and the list's summaries cannot
          // show. Restores are rare enough that the refetch costs nothing.
          // The restored event doesn't carry projectId, so we MUST
          // check projectId after the fetch — without it, a restore
          // in a different project would trigger an unnecessary
          // refetch of this project's lists AND its archive cache.
          api.getTicket(e.ticketId).then((ticket) => {
            if (ticket.projectId !== projectId) return;
            qc.invalidateQueries(lists);
            qc.invalidateQueries({ queryKey: ['tickets', projectId, 'archived'] });
          }).catch(() => {
            qc.invalidateQueries(lists);
          });
          return;
        }

        case 'ticket.deleted': {
          noteRemoved(e.ticketId);
          qc.setQueriesData<TicketSummary[]>(lists, (old) => {
            if (!old || !old.some((t) => t.id === e.ticketId)) return old;
            return old.filter((t) => t.id !== e.ticketId);
          });
          return;
        }

        case 'comment.created': {
          // Bump commentCount on the matching summary. Flip
          // hasAiActivity if the comment is from an MCP client (an AI
          // agent worked the ticket). Both fields drive board card
          // badges; updating them directly avoids a refetch just to
          // see "comment count went up by 1".
          //
          // Note: not idempotent on duplicate event delivery (would
          // double-count). Live bus doesn't dedupe today; if this
          // becomes a real issue, add a per-comment-id seen-set or
          // include comment count delta in the event payload.
          qc.setQueriesData<TicketSummary[]>(lists, (old) => {
            if (!old || !old.some((t) => t.id === e.ticketId)) return old;
            return old.map((t) => {
              if (t.id !== e.ticketId) return t;
              const next: TicketSummary = {
                ...t,
                commentCount: (t.commentCount ?? 0) + 1,
                // The agent at work commenting keeps its shimmer going (t-gdi2Cd).
                agentWorking: agentAfterComment(t, e.comment),
              };
              if (e.comment.authorType === 'mcp_client') {
                next.hasAiActivity = true;
              }
              return next;
            });
          });
          return;
        }

        case 'capture.ready':
        case 'annotation.updated':
          // No board impact. `capture.ready` only changes DevTools
          // data which the board summary doesn't display.
          // `annotation.updated` is handled by AnnotationCanvas
          // directly (it manages its own merge logic).
          return;

        case 'member.access_changed':
          // Member's project-access scope changed. PR1 doesn't broadcast
          // this event yet — server-side WS filter + invalidation lands
          // in PR2. The case exists here so the exhaustiveness check
          // below stays satisfied after the LiveEvent union widened.
          return;

        default: {
          // Exhaustiveness check — if a new LiveEvent variant is added
          // to packages/types/src/index.ts, TypeScript errors here so
          // the new case can't silently fall through unhandled. The
          // `_exhaustive` assignment must be `never`; if `e` ever
          // narrows to anything else, compilation fails.
          const _exhaustive: never = e;
          void _exhaustive;
          return;
        }
      }
    });
  }, [projectId, qc]);
  const prefs = useBoardPrefs((s) => (projectId ? s.get(projectId) : null));
  const patch = useBoardPrefs((s) => s.patch);
  const patchPrefs = (p: Partial<ProjectViewPrefs>) => { if (projectId) patch(projectId, p); };

  const [statusTicket, setStatusTicket] = useState<{ t: TicketSummary; anchor: DOMRect } | null>(null);
  const [typeTicket, setTypeTicket] = useState<{ t: TicketSummary; anchor: DOMRect } | null>(null);
  const [overflowTicket, setOverflowTicket] = useState<{ t: TicketSummary; anchor: DOMRect } | null>(null);
  const [assignTicket, setAssignTicket] = useState<{ t: TicketSummary; anchor: DOMRect } | null>(null);
  // Per-column header menu + inline add-ticket prompt. The anchor is a DOMRect
  // (not an HTMLElement) so the Popover component can position against a fixed
  // rectangle even if the underlying element scrolls away.
  const [columnMenu, setColumnMenu] = useState<{ columnId: string; anchor: DOMRect } | null>(null);
  const [addTicket, setAddTicket] = useState<{ columnId: string; anchor: DOMRect } | null>(null);
  const [addDraft, setAddDraft] = useState('');
  const [query, setQuery] = useState('');
  // The header's people, clicked to show only their tickets (TOADO-800).
  // Deliberately not a saved preference: a refresh, or leaving the board,
  // clears it. Several at once show anyone's tickets among them.
  const [peopleFilter, setPeopleFilter] = useState<string[]>([]);
  useEffect(() => { setPeopleFilter([]); }, [projectId]);
  // A selection belongs to its board: carried to another project, Move… there
  // would send that board's tickets again (TOADO-976).
  useEffect(() => { setBulkSelection(new Set()); }, [projectId]);
  const togglePerson = (userId: string) => setPeopleFilter((now) => (
    now.includes(userId) ? now.filter((id) => id !== userId) : [...now, userId]
  ));
  const [editingUrl, setEditingUrl] = useState(false);
  const [urlDraft, setUrlDraft] = useState('');
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTicketTarget, setMoveTicketTarget] = useState<TicketSummary | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTicketTarget, setDeleteTicketTarget] = useState<TicketSummary | null>(null);
  const [branchOpen, setBranchOpen] = useState(false);
  const [branchTicketTarget, setBranchTicketTarget] = useState<TicketSummary | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  // The Dashboard's New ticket opens the board with ?new=1 (TOADO-731): the
  // create modal opens, and the address loses the flag so a reload does not.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('new') !== '1') return;
    setCreateOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  // How this board works, and the instruction for an agent (TOADO-972).
  // Keyboard focus model for Cards / Table — j/k cursor, Enter opens.
  // SingleView has its own j/k via useShortcutMap; the page-level
  // bindings below skip when viewMode === 'single' to avoid collision.
  const [focusedTicketId, setFocusedTicketId] = useState<string | null>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Bulk-select state — Set of ticket ids checked via the per-card checkbox.
  // Only populated on board/cards views; single view doesn't expose the
  // checkbox at all, so this stays empty there.
  const [bulkSelection, setBulkSelection] = useState<Set<string>>(new Set());
  const toolbarRow = useRef<HTMLDivElement>(null);
  const bulkBar = useRef<HTMLDivElement>(null);
  useInertUnderBar(toolbarRow, bulkBar, bulkSelection.size > 0);
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);
  const [bulkAssignAnchor, setBulkAssignAnchor] = useState<DOMRect | null>(null);
  const [bulkTypeAnchor, setBulkTypeAnchor] = useState<DOMRect | null>(null);
  // Tickets currently in flight on a bulk operation — passed down to
  // BoardView/CardsView/TableView/SingleView so they can render a dim+
  // spinner overlay on each affected card. Cleared after the mutation's
  // refetch settles so the visual flips back to normal once the server
  // state catches up. Set both bulk-move and bulk-archive flows; bulk-
  // assign is fast enough not to need it.
  // Per-card "in-flight" indicator — every card-mutating action
  // (move, archive, delete, restore, assign) marks its ticket id(s)
  // here so the views can render a dim+spinner overlay on the card.
  // Without this, slow mutations look like the click did nothing
  // until the optimistic update lands. Cleared on settle.
  const [movingTicketIds, setMovingTicketIds] = useState<Set<string>>(new Set());
  const markPending = (ids: string | string[]) => {
    const arr = Array.isArray(ids) ? ids : [ids];
    setMovingTicketIds((prev) => {
      const n = new Set(prev);
      for (const id of arr) n.add(id);
      return n;
    });
  };
  const unmarkPending = (ids: string | string[]) => {
    const arr = Array.isArray(ids) ? ids : [ids];
    setMovingTicketIds((prev) => {
      const n = new Set(prev);
      for (const id of arr) n.delete(id);
      return n;
    });
  };

  const toggleBulk = (ticketId: string, next: boolean) => {
    setBulkSelection((prev) => {
      const s = new Set(prev);
      if (next) s.add(ticketId);
      else s.delete(ticketId);
      return s;
    });
  };
  const clearBulk = () => setBulkSelection(new Set());

  const assignMutation = useMutation({
    mutationFn: ({ ticketId, userIds }: { ticketId: string; userIds: string[] }) =>
      api.assignTicket(ticketId, userIds, 'replace'),
    onMutate: ({ ticketId }) => { markPending(ticketId); },
    onSettled: (_data, _err, vars) => { unmarkPending(vars.ticketId); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tickets', projectId] }); },
  });

  // Bulk archive — fan out individual /archive calls (no bulk endpoint yet)
  // and collect results. Partial failures are surfaced in the toast so the
  // user knows exactly how many landed; the ones that succeeded come off
  // the board optimistically via cache invalidation.
  const bulkArchiveMutation = useMutation({
    mutationFn: async (ticketIds: string[]) => {
      // Mark all selected tickets as in-flight so each card renders
      // the dim+spinner overlay during the fan-out. Especially useful
      // when archiving a large batch ("we may move a lot at one time
      // to archive") — without this, the user sees no feedback until
      // the cards disappear.
      markPending(ticketIds);
      const results = await settleWithLimit(ticketIds, BULK_REQUEST_LIMIT, (id) => api.archiveTicket(id));
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - ok;
      return { ok, failed };
    },
    onSettled: (_data, _err, ticketIds) => { unmarkPending(ticketIds); },
    onSuccess: ({ ok, failed }) => {
      qc.invalidateQueries({ queryKey: ['tickets', projectId] });
      qc.invalidateQueries({ queryKey: ['tickets', projectId, 'archived'] });
      clearBulk();
      if (failed === 0) {
        toast.push({ variant: 'success', title: `Archived ${ok} ticket${ok === 1 ? '' : 's'}` });
      } else {
        toast.push({
          variant: 'warning',
          title: `Archived ${ok}, ${failed} failed`,
          description: 'Some tickets could not be archived. Try again for the rest.',
        });
      }
    },
    onError: (err) => {
      toast.push({ variant: 'danger', title: 'Bulk archive failed', description: (err as Error).message });
    },
  });

  // Sending to Jira (TOADO-708): offered only where this project can send and
  // this person may. The server decides each ticket again, and its live word
  // updates the cards; the toast says what happened to the batch.
  const jiraSend = useQuery({ queryKey: ['jira-send', projectId], queryFn: () => api.getProjectJiraSend(projectId!),
    enabled: !!projectId, staleTime: 60_000 });
  const canSendToJira = jiraSend.data?.canSend === true;
  const canUnlinkFromJira = jiraSend.data?.canUnlink === true;
  const sendToJiraMutation = useMutation({
    mutationFn: (ticketIds: string[]) => sendInBatches(ticketIds, (batch) => api.sendTicketsToJira(projectId!, batch)),
    onSuccess: (counts, ticketIds) => {
      if (ticketIds.length > 1) clearBulk();
      // A ticket page opened next reads where its ticket now stands.
      void qc.invalidateQueries({ queryKey: ['jira-export'] });
      const said = sentToJiraSummary(counts);
      toast.push({ variant: said.variant, title: said.title, description: said.description });
    },
    onError: (error) => {
      // A project that stopped being able to send says so on the next look.
      qc.invalidateQueries({ queryKey: ['jira-send', projectId] });
      toast.push({ variant: 'danger', title: 'Could not send to Jira', description: (error as Error).message });
    },
  });
  // Forgetting a card's Jira issue: one deleted in Jira, or the wrong one. The
  // live word clears the card; the refetch is for a board that missed it.
  const unlinkFromJiraMutation = useMutation({
    mutationFn: (ticket: TicketSummary) => api.unlinkTicketFromJira(ticket.id),
    onSuccess: (_answer, ticket) => {
      void qc.invalidateQueries({ queryKey: ['tickets', projectId] });
      void qc.invalidateQueries({ queryKey: ['jira-export', ticket.id] });
      toast.push({ variant: 'success', title: `Unlinked from ${ticket.jira?.issueKey ?? 'Jira'}`,
        description: 'Jira is not changed. Sending again links this issue back while it is still in Jira, or makes a new one if it was deleted.' });
    },
    onError: (error) => toast.push({ variant: 'danger', title: 'Could not unlink from Jira', description: (error as Error).message }),
  });
  // Several at once (TOADO-849): cards whose issues were deleted in Jira, or
  // sent by mistake. Six requests at a time, as every bulk action is.
  const bulkUnlinkMutation = useMutation({
    mutationFn: async (ticketIds: string[]) => {
      const results = await settleWithLimit(ticketIds, BULK_REQUEST_LIMIT, (id) => api.unlinkTicketFromJira(id));
      const ok = results.filter((result) => result.status === 'fulfilled').length;
      return { ok, failed: results.length - ok };
    },
    onSuccess: ({ ok, failed }) => {
      clearBulk();
      void qc.invalidateQueries({ queryKey: ['tickets', projectId] });
      void qc.invalidateQueries({ queryKey: ['jira-export'] });
      toast.push(unlinkedSummary(ok, failed));
    },
    onError: (error) => toast.push({ variant: 'danger', title: 'Could not unlink from Jira', description: (error as Error).message }),
  });
  const [unlinkAsk, setUnlinkAsk] = useState<string[] | null>(null);
  const [bigSendAsk, setBigSendAsk] = useState<{ ids: string[]; title: string; description: string } | null>(null);

  // Single-ticket archive from the card overflow menu. Same cache-
  // invalidation pattern as the bulk version, plus an Undo action that
  // calls /tickets/:id/restore through the same toast surface bulk uses.
  const archiveMutation = useMutation({
    mutationFn: (ticket: TicketSummary) => api.archiveTicket(ticket.id).then(() => ticket),
    onMutate: (ticket) => { markPending(ticket.id); },
    onSettled: (_data, _err, ticket) => { unmarkPending(ticket.id); },
    onSuccess: (ticket) => {
      qc.invalidateQueries({ queryKey: ['tickets', projectId] });
      qc.invalidateQueries({ queryKey: ['tickets', projectId, 'archived'] });
      toast.push({
        variant: 'success',
        title: 'Archived',
        description: ticket.title.slice(0, 60),
        action: {
          label: 'Undo',
          onClick: () => {
            api.restoreTicket(ticket.id).then(() => {
              qc.invalidateQueries({ queryKey: ['tickets', projectId] });
              qc.invalidateQueries({ queryKey: ['tickets', projectId, 'archived'] });
              toast.push({ variant: 'info', title: 'Restored' });
            }).catch((err) => {
              toast.push({ variant: 'danger', title: 'Restore failed', description: (err as Error).message });
            });
          },
        },
      });
    },
    onError: (err) => {
      toast.push({ variant: 'danger', title: 'Archive failed', description: (err as Error).message });
    },
  });

  /**
   * Bulk move: every ticket to one column (picked in MoveToProjectModal), in
   * bulk requests of 50, reported in the outcomes `features/board/bulkMove.ts`
   * sorts it into (TOADO-976), each said in the toast as what it means for the
   * person: what they can move again, what they may not, and what to check
   * first. Nothing is put back in the selection for another try: whoever
   * presses Move… again picks the tickets from the board as it now is.
   *
   * The lists are refetched and awaited before anything is said, so the toast
   * never pops over cards still in the old column (2026-05-01, a 50+ ticket
   * move), and a refresh that did not land, after moves that were or may have
   * been stored, is said as that, with a Refresh that only reloads the lists.
   * Moves are never replayed to mend a display.
   *
   * The project the batch was started on travels with it: the page can be
   * showing another project by the time it settles, and the selection it
   * leaves belongs only to the board it came from.
   */
  const bulkMoveMutation = useMutation({
    mutationFn: async (vars: { ticketIds: string[]; columnId: string; projectId?: string; sourceProjectId: string }) => {
      // Mark tickets as in-flight so the views render dim+spinner on
      // each affected card. Set BEFORE the network calls fire so the
      // user sees feedback immediately. Cleared in onSuccess/onError.
      setMovingTicketIds(new Set(vars.ticketIds));
      const target = { columnId: vars.columnId, projectId: vars.projectId };
      const outcome = await moveTickets(vars.ticketIds, {
        many: async (ids) => (await api.bulkMoveTickets(ids, target)).results,
        one: (id) => api.moveTicket(id, target),
      });
      // A refresh that throws is one that did not land: the moves are stored
      // either way, and must still be reported as moves rather than a failure.
      const refreshed = await refreshTicketLists(qc, [vars.sourceProjectId, vars.projectId ?? vars.sourceProjectId]).catch(() => false);
      return { ...outcome, refreshed };
    },
    onSuccess: ({ moved, refused, signedOut, forbidden, unconfirmed, notYet, notYetBecause, refreshed }, vars) => {
      setMovingTicketIds(new Set());
      setBulkMoveOpen(false);
      // The batch leaves the selection, refused tickets included, and anything
      // selected meanwhile stays. Putting the refused ones back was tried: by
      // the time a batch settles a ticket can have been moved another way, and
      // "Move… again" would then repeat a stored move (Codex rounds 2 to 5).
      // Taking tickets out of a selection can never send anything twice.
      if (vars.sourceProjectId === projectId) {
        setBulkSelection((current) => new Set([...current].filter((id) => !vars.ticketIds.includes(id))));
      }
      const plural = (n: number) => (n === 1 ? '' : 's');
      const title = [
        `Moved ${moved.length}${refused.length + signedOut.length + forbidden.length + unconfirmed.length + notYet.length === 0 ? ` ticket${plural(moved.length)}` : ''}`,
        ...(notYet.length ? [`${notYet.length} not moved yet`] : []),
        ...(refused.length ? [`${refused.length} refused`] : []),
        ...(signedOut.length ? [`${signedOut.length} not moved`] : []),
        ...(forbidden.length ? [`${forbidden.length} not allowed`] : []),
        ...(unconfirmed.length ? [`${unconfirmed.length} not confirmed`] : []),
      ].join(', ');
      const notes = [
        // Said with the wait it needs: told only "refused", the person tried
        // again at once and was refused again (TOADO-996, review round 4).
        ...(notYet.length ? [notYetBecause === 'limit'
          ? `The limit on moves a minute was reached, so ${notYet.length === 1 ? 'one was' : `${notYet.length} were`} not moved. Wait a minute, then move ${notYet.length === 1 ? 'it' : 'them'} again.`
          : `Bulk move was unavailable for a moment, so ${notYet.length === 1 ? 'one was' : `${notYet.length} were`} not moved. Try ${notYet.length === 1 ? 'it' : 'them'} again shortly.`] : []),
        // A refusal says the request was refused, not where the ticket is now.
        ...(refused.length ? [`The server refused ${refused.length === 1 ? 'one move' : `${refused.length} moves`}: check the board and select any you still want to move.`] : []),
        // Worded for what the answer proves: the sign-in was refused, which is
        // usually a session that ended, and can be a deploy's cold server.
        ...(signedOut.length ? [`Your sign-in was refused for ${signedOut.length === 1 ? 'one of them' : `${signedOut.length} of them`}, so ${signedOut.length === 1 ? 'it did' : 'they did'} not move. Check you are signed in, then move ${signedOut.length === 1 ? 'it' : 'them'} again.`] : []),
        // Not offered again: it would be refused the same way.
        ...(forbidden.length ? [`You are not allowed to move ${forbidden.length === 1 ? 'one of them' : `${forbidden.length} of them`}, so ${forbidden.length === 1 ? 'it stays' : 'they stay'} where ${forbidden.length === 1 ? 'it was' : 'they were'}.`] : []),
        ...(unconfirmed.length ? [`${unconfirmed.length === 1 ? 'One' : unconfirmed.length} got no clear answer and may have moved: check the board before moving ${unconfirmed.length === 1 ? 'it' : 'them'} again.`] : []),
      ];
      // This batch's stale-board toast, while one is up, whether its Refresh is
      // running (a second press waits for the first), and the projects its
      // refresh reloads (the stale toasts it answers).
      let staleId = '';
      let refreshing = false;
      const projects = [vars.sourceProjectId, vars.projectId ?? vars.sourceProjectId];
      const clearStale = () => {
        for (const id of staleBoards.answeredBy(projects)) {
          staleBoards.forget(id);
          toast.dismiss(id);
        }
      };
      // A refresh that landed, here or by another batch's button, leaves these
      // projects' boards stale no longer; other projects' toasts stay.
      if (refreshed) clearStale();
      // The board is stale only if something was stored, or may have been. A
      // failed refresh does not say why it failed (refused, slow, or down), so
      // the toast does not guess: it offers the Refresh, and a Refresh that
      // fails again says what to check.
      const mayHaveMoved = moved.length + unconfirmed.length > 0;
      // What the batch did, kept on every stale toast it puts up, those a
      // failed Refresh puts up again included.
      const staleNotes = notes.length ? notes : ['The moves are stored; the board still shows what it had before.'];
      if (!refreshed && mayHaveMoved) {
        showStale(`${title}, but the board did not refresh`, staleNotes.join(' '));
        return;
      }
      /**
       * One toast for this batch's stale board, replaced rather than stacked:
       * each failed Refresh puts up the same toast again, still carrying what
       * the batch did, and a Refresh that lands takes it down.
       */
      function showStale(staleTitle: string, description: string): void {
        if (staleId) {
          staleBoards.forget(staleId);
          toast.dismiss(staleId);
        }
        const id: string = toast.push({
          variant: 'warning', title: staleTitle, description, action: refreshAction(),
          onDismiss: () => {
            staleBoards.forget(id);
            if (staleId === id) staleId = '';
          },
        });
        staleId = id;
        staleBoards.add(id, projects);
      }
      /** The advice is ordered rather than a diagnosis: a failed refresh does not say why. */
      function refreshAction(): { label: string; onClick: () => void } {
        return {
          label: 'Refresh the board',
          onClick: () => {
            // A second press of this toast while its refresh runs is not another
            // refresh. Refreshes from different toasts and bulk moves queue
            // one behind another (refreshTicketLists), so none cancels another.
            if (refreshing) return;
            refreshing = true;
            void refreshTicketLists(qc, projects)
              .catch(() => false)
              .then((ok) => {
                refreshing = false;
                if (ok) {
                  // Every stale toast for these projects goes, whichever batch left it.
                  clearStale();
                  toast.push({ variant: 'success', title: 'The board is up to date' });
                  return;
                }
                // Already answered meanwhile (another refresh landed and took
                // this toast down): nothing is stale to say again.
                if (!staleBoards.isStale(staleId)) return;
                // The sign-in advice once: a batch with a refused sign-in already says it.
                const advice = signedOut.length
                  ? 'Try again in a moment.'
                  : 'Try again in a moment; if it still does not refresh, check you are signed in.';
                showStale(`${title}, but the board still did not refresh`, [...staleNotes, advice].join(' '));
              });
          },
        };
      }
      toast.push(notes.length
        ? { variant: 'warning', title, description: notes.join(' ') }
        : { variant: 'success', title });
    },
    onError: (err) => {
      setMovingTicketIds(new Set());
      toast.push({ variant: 'danger', title: 'Bulk move failed', description: (err as Error).message });
    },
  });

  /** The one way a bulk move starts: never while another is running. */
  function startBulkMove(ticketIds: string[], columnId: string, targetProjectId?: string) {
    if (bulkMoveMutation.isPending || ticketIds.length === 0) return;
    bulkMoveMutation.mutate({ ticketIds, columnId, projectId: targetProjectId, sourceProjectId: projectId ?? '' });
  }

  // Bulk assign / reassign / unassign across the checked tickets.
  // Mode mirrors the AssigneePicker radios:
  //   replace: clear each ticket's assignees, set to the picked set.
  //   add:     append picked to each ticket (no-op if already there).
  //   remove:  delete picked from each ticket's existing list.
  // Same fan-out, settled per ticket and bounded (settleWithLimit), as the
  // other bulk actions so a single bad request doesn't poison the whole batch.
  const bulkAssignMutation = useMutation({
    mutationFn: async (vars: { ticketIds: string[]; userIds: string[]; mode: 'add' | 'replace' | 'remove' }) => {
      const call = (id: string) => vars.mode === 'remove'
        ? api.unassignTicket(id, vars.userIds)
        : api.assignTicket(id, vars.userIds, vars.mode);
      const results = await settleWithLimit(vars.ticketIds, BULK_REQUEST_LIMIT, call);
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      return { ok, failed: results.length - ok, mode: vars.mode };
    },
    onSuccess: ({ ok, failed, mode }) => {
      qc.invalidateQueries({ queryKey: ['tickets', projectId] });
      clearBulk();
      setBulkAssignAnchor(null);
      const verb = mode === 'remove' ? 'Unassigned' : mode === 'add' ? 'Updated' : 'Assigned';
      toast.push({
        variant: failed === 0 ? 'success' : 'warning',
        title: failed === 0
          ? `${verb} ${ok} ticket${ok === 1 ? '' : 's'}`
          : `${verb} ${ok}, ${failed} failed`,
      });
    },
    onError: (err) => toast.push({ variant: 'danger', title: 'Bulk assign failed', description: (err as Error).message }),
  });

  /**
   * Retype the selected tickets, or clear their type with null. One request per
   * ticket through the ordinary update, the same fan-out and partial-failure
   * report as bulk assign. It is how a type that tickets use is emptied before
   * it can be deleted from a project's settings (Toado ticket t-ER8UEc).
   *
   * The tickets that failed stay selected, so trying again is one more pick
   * rather than finding them by hand.
   */
  const bulkTypeMutation = useMutation({
    mutationFn: async (vars: { ticketIds: string[]; typeId: string | null; typeName: string }) => {
      const results = await settleWithLimit(vars.ticketIds, BULK_REQUEST_LIMIT, (id) => api.updateTicket(id, { typeId: vars.typeId }));
      const failedIds = vars.ticketIds.filter((_, index) => results[index]!.status === 'rejected');
      return { ok: results.length - failedIds.length, failedIds, typeName: vars.typeName };
    },
    onSuccess: ({ ok, failedIds, typeName }) => {
      qc.invalidateQueries({ queryKey: ['tickets', projectId] });
      qc.invalidateQueries({ queryKey: ['ticketTypeUsage', projectId] });
      setBulkSelection(new Set(failedIds));
      toast.push(failedIds.length === 0
        ? { variant: 'success', title: `${typeName} on ${ok} ticket${ok === 1 ? '' : 's'}` }
        : {
          variant: 'warning',
          title: `${typeName} on ${ok}, ${failedIds.length} failed`,
          description: `The ${failedIds.length === 1 ? 'one that failed is' : 'ones that failed are'} still selected.`,
        });
    },
    onError: (err) => toast.push({ variant: 'danger', title: 'Could not change the type', description: (err as Error).message }),
  });
  /**
   * The picker closes before the requests go, and a second pick while a batch
   * is out is refused. Two batches racing ticket by ticket left a mix of both
   * types, each one reporting success.
   */
  const retypeSelection = (typeId: string | null, typeName: string) => {
    if (bulkTypeMutation.isPending) return;
    setBulkTypeAnchor(null);
    bulkTypeMutation.mutate({ ticketIds: Array.from(bulkSelection), typeId, typeName });
  };

  /**
   * One card's type, from its type pill. The card shows the pick at once, in
   * the unfiltered lists only, as the live `ticket.updated` handler writes it;
   * the type-filtered lists are refetched instead, since a retype changes which
   * of them the ticket belongs in. A failure puts back the type the card had,
   * unless something newer has already replaced the one written here.
   */
  const retypeMutation = useMutation({
    mutationFn: ({ ticket, type }: { ticket: TicketSummary; type: TicketTypeRef | null }) =>
      api.updateTicket(ticket.id, { typeId: type?.id ?? null }),
    onMutate: async ({ ticket, type }) => {
      const lists = { queryKey: boardTicketListsKey(projectId), predicate: (q: Query) => !isTypeFilteredList(q.queryKey) };
      await qc.cancelQueries(lists);
      qc.setQueriesData<TicketSummary[]>(lists, (old) => old && withType(old, ticket.id, type ?? undefined));
    },
    onError: (err, { ticket, type }) => {
      const lists = { queryKey: boardTicketListsKey(projectId), predicate: (q: Query) => !isTypeFilteredList(q.queryKey) };
      qc.setQueriesData<TicketSummary[]>(lists, (old) => {
        const shown = old?.find((t) => t.id === ticket.id);
        return old && shown && shown.type?.id === type?.id ? withType(old, ticket.id, ticket.type) : old;
      });
      toast.push({ variant: 'danger', title: 'Could not change the type', description: (err as Error).message });
    },
    onSettled: (_data, _err, { ticket }) => {
      qc.invalidateQueries({ queryKey: boardTicketListsKey(projectId), predicate: (q) => isTypeFilteredList(q.queryKey) });
      qc.invalidateQueries({ queryKey: ['ticket', ticket.id] });
      qc.invalidateQueries({ queryKey: ['ticketTypeUsage', projectId] });
    },
  });
  /** The menu closes on a pick; a second pick for a card whose first is still out is dropped. */
  const retypeTicket = (ticket: TicketSummary, type: TicketTypeRef | null) => {
    setTypeTicket(null);
    if ((ticket.type?.id ?? null) === (type?.id ?? null)) return;
    if (retypeMutation.isPending && retypeMutation.variables?.ticket.id === ticket.id) return;
    retypeMutation.mutate({ ticket, type });
  };

  const moveMutation = useMutation({
    mutationKey: moveKey,
    // The server is told `anchor`, the card beside the drop, or nothing, which
    // puts the card at the end of the column: never an index among the cards
    // shown, which there would count the tickets the board hides (see
    // `dropAnchor`). The cached lists are placed from the same anchor, so the
    // refetch that follows brings the order already on screen.
    mutationFn: ({ ticketId, columnId, targetProjectId, anchor }: {
      ticketId: string; columnId: string; targetProjectId?: string; anchor?: DropAnchor;
    }) =>
      api.moveTicket(ticketId, { columnId, projectId: targetProjectId, ...anchor }),
    onMutate: async ({ ticketId, columnId, targetProjectId, anchor }) => {
      // Surface the in-flight state on the moved card so single-drag
      // and 3-dot-menu moves get the same spinner overlay the bulk
      // path already shows.
      markPending(ticketId);
      removedDuringMove.current.delete(ticketId);
      // Every board list of the project, one per type filter the person has
      // used: the one on screen, and any they may switch back to.
      const lists = { queryKey: boardTicketListsKey(projectId) };
      await qc.cancelQueries(lists);
      const prev = qc.getQueriesData<TicketSummary[]>(lists);
      if (targetProjectId && targetProjectId !== projectId) {
        // Cross-project move: optimistically drop from this board so the
        // card doesn't ghost-linger. The destination board's next render
        // will fetch fresh data.
        qc.setQueriesData<TicketSummary[]>(lists, (old) => old?.filter((t) => t.id !== ticketId));
      } else {
        // BoardView takes its order from this list once the drag is over, so
        // the card has to be where it was dropped, or it jumps back until
        // the refetch.
        qc.setQueriesData<TicketSummary[]>(lists, (old) => old && placeAtAnchor(old, ticketId, columnId as ColumnId, anchor));
      }
      return { prev };
    },
    onError: (_err, { ticketId, columnId, targetProjectId }, ctx) => {
      // Only the moved card goes back. Putting back the whole lists as they
      // were before the move would also undo whatever live events changed
      // while it was in flight: a teammate's delete would come back. A refused
      // move usually means the board's picture was out of date, so the lists
      // are refetched when the last move settles (below).
      const removedByTheMove = !!targetProjectId && targetProjectId !== projectId
        && !removedDuringMove.current.has(ticketId);
      ctx?.prev.forEach(([key, previous]) => {
        if (previous) {
          qc.setQueryData<TicketSummary[]>(key, (current) =>
            current && restorePlace(current, previous, ticketId, { placedIn: columnId, removedByTheMove }));
        }
      });
      toast.push({ variant: 'danger', title: 'Move failed', description: 'The card is back where it was. Try again.' });
    },
    onSettled: (_data, _err, vars) => {
      unmarkPending(vars.ticketId);
      removedDuringMove.current.delete(vars.ticketId);
      // After a failure too: a move whose answer was lost may have landed.
      if (vars.targetProjectId && vars.targetProjectId !== projectId) {
        qc.invalidateQueries({ queryKey: ['tickets', vars.targetProjectId] });
      }
      // Only the last move to settle refetches. A refetch while another move
      // is in flight brings back the order from before that one, and its
      // card jumps back until it settles too. This one still counts itself.
      if (qc.isMutating({ mutationKey: moveKey }) === 1) {
        qc.invalidateQueries({ queryKey: ['tickets', projectId] });
      }
    },
  });

  const createTicketMutation = useMutation({
    mutationFn: (vars: { title: string; columnId?: string }) =>
      api.createTextTicket(projectId!, vars.title, vars.columnId),
    onSuccess: (ticket) => {
      qc.invalidateQueries({ queryKey: ['tickets', projectId] });
      setCreateOpen(false);
      toast.push({ variant: 'success', title: 'Ticket created' });
      openTicketOverBoard(ticket.id);
    },
    onError: (err) => {
      toast.push({ variant: 'danger', title: 'Create failed', description: (err as Error).message });
    },
  });

  // Column header actions — rename + delete go through the existing
  // replaceColumns endpoint so tickets in a deleted column get reassigned
  // automatically (the endpoint reassigns to the leftmost remaining column).
  const renameColumnMutation = useMutation({
    mutationFn: async (vars: { columnId: string; name: string }) => {
      const existing = columns.data ?? [];
      const next = existing.map((c) => (c.id === vars.columnId ? { ...c, name: vars.name } : c));
      return api.replaceColumns(projectId!, next.map((c, i) => ({ id: c.id, name: c.name, order: i })));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['columns', projectId] });
      qc.invalidateQueries({ queryKey: ['tickets', projectId] });
    },
    onError: (err) => {
      toast.push({ variant: 'danger', title: 'Rename failed', description: (err as Error).message });
    },
  });

  const handleAddTicket = (columnId: string, anchorEl?: HTMLElement) => {
    setAddDraft('');
    if (anchorEl) {
      setAddTicket({ columnId, anchor: anchorEl.getBoundingClientRect() });
    } else {
      // Fallback: called from the column's menu, not directly. Position
      // against the column's header if we can find it.
      const header = document.querySelector(`[data-column-id="${columnId}"]`);
      const rect = header?.getBoundingClientRect() ?? new DOMRect(100, 100, 0, 0);
      setAddTicket({ columnId, anchor: rect });
    }
  };

  const submitAddTicket = () => {
    const title = addDraft.trim();
    if (!title || !addTicket) return;
    createTicketMutation.mutate({ title, columnId: addTicket.columnId });
    setAddTicket(null);
    setAddDraft('');
  };

  const handleColumnMenu = (columnId: string, anchorEl: HTMLElement) => {
    setColumnMenu({ columnId, anchor: anchorEl.getBoundingClientRect() });
  };

  const handleRenameColumn = (columnId: string) => {
    const existing = columns.data?.find((c) => c.id === columnId);
    if (!existing) return;
    // Browser prompt is intentionally minimal — the settings page is the
    // place for anything more structured. This is a quick rename.
    const next = window.prompt('Rename column', existing.name);
    if (!next || next.trim() === existing.name) return;
    renameColumnMutation.mutate({ columnId, name: next.trim() });
  };

  useShortcutMap({
    // Direct view-mode switches. Cycling via V is also kept (below) for
    // power users who like rotate-through; new users find 1-4 obvious.
    '1': () => prefs && patchPrefs({ viewMode: 'board' }),
    '2': () => prefs && patchPrefs({ viewMode: 'cards' }),
    '3': () => prefs && patchPrefs({ viewMode: 'table' }),
    '4': () => prefs && patchPrefs({ viewMode: 'single' }),
    v: () => {
      if (!prefs) return;
      const order = ['board', 'cards', 'single', 'table'] as const;
      const i = order.indexOf(prefs.viewMode);
      const next = order[(i + 1) % order.length];
      if (next) patchPrefs({ viewMode: next });
    },
    t: () => prefs && patchPrefs({ thumbnails: !prefs.thumbnails }),
    '/': (e) => {
      // Standard `/` to focus search. preventDefault stops the literal
      // '/' from typing into the input on focus.
      e.preventDefault();
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    },
    // `?` is owned by Shell so exactly one shortcuts dialog opens. The
    // toolbar button below still opens this page's detailed cheatsheet.
    Escape: (e) => {
      // Layered fallthrough: clear search → close advanced-search →
      // close shortcuts modal. Modals already have their own Escape
      // handlers; this is the page-level handler that fires only when
      // no modal owns the key. Claimed when it clears the search, so a
      // listener after this one (an onboarding tour) knows the key was used.
      if (query) {
        e.preventDefault();
        setQuery('');
      }
    },
    // J / K navigate the focused-ticket cursor in Cards + Table views.
    // Single view has its own j/k via SingleView's useShortcutMap that
    // operates on the queue list — gate here so we don't double-fire.
    // Board view has no per-card focus model (drag-and-drop is the
    // interaction), so j/k no-op there.
    j: () => moveFocus(1),
    k: () => moveFocus(-1),
    ArrowDown: () => moveFocus(1),
    ArrowUp: () => moveFocus(-1),
    Enter: () => {
      if (prefs?.viewMode !== 'cards' && prefs?.viewMode !== 'table') return;
      if (focusedTicketId) openTicketOverBoard(focusedTicketId);
    },
  }, { page: true });

  // moveFocus is defined further below where filteredTickets is in scope.
  // It's a regular function declaration (not a hook), so its lexical
  // position doesn't matter for hook ordering — it's reachable from
  // the useShortcutMap callbacks above via closure.
  //
  // The focus-reset effect, by contrast, MUST live above the early
  // return below — otherwise hooks count differs between loading and
  // loaded renders, which triggers React error #310. So this effect
  // gets the "lighter" check: clear focus if the focused ticket no
  // longer appears in tickets.data (it was deleted, project switched,
  // or initial load hadn't populated yet). The "ghost focus on
  // filter-out" case is benign — CardsView/TableView won't render
  // the focus ring on a hidden ticket — so we don't reset on filter
  // change. Pressing J/K refocuses if needed.
  useEffect(() => {
    if (!focusedTicketId || !tickets.data) return;
    if (!tickets.data.some((t) => t.id === focusedTicketId)) {
      setFocusedTicketId(null);
    }
  }, [tickets.data, focusedTicketId]);

  // creatorsById and membersById are the same Map — workspace members
  // are the only set of users who can author tickets — so use the
  // hook's usersById for both. Earlier this page derived the Map
  // inline; moved into useBoardData (board hook earns its keep).
  const creatorsById = usersById;

  // Server-side search: when the user types ≥2 chars, hit /search/tickets
  // so we match against description + comments + URL, not just title.
  // Below 2 chars the api requires it anyway and we fall back to
  // client-side filtering of the loaded tickets. The debounce avoids
  // firing one fetch per keystroke.
  //
  // Search results REPLACE the source list when active (not just an
  // id-intersect over `tickets.data`). The board only loads active
  // tickets — `useBoardData` calls `listTickets(projectId)` with no
  // status param, so the api defaults to 'active'. With `includeArchived
  // = true`, the api returns archived rows; an intersect would silently
  // drop them. Treating the search result as the new source set fixes
  // that AND lets the api's project / token / archive filters be the
  // source of truth.
  const debouncedQuery = useDebouncedValue(query, 250);
  const searchActive = debouncedQuery.trim().length >= 2;
  const searchQ = useQuery({
    queryKey: projectTicketSearchKey(projectId, debouncedQuery, prefs?.quickFilters.archived),
    queryFn: () => api.searchTickets(debouncedQuery, {
      projectId,
      includeArchived: !!prefs?.quickFilters.archived,
      limit: 100,
    }),
    enabled: searchActive && !!projectId,
    staleTime: 30_000,
  });

  // Rebuilt only when what it is built from changes, not on every render.
  // BoardView takes a new list as a new order from the server (see its sync
  // effect), so a list rebuilt by an unrelated re-render, such as the pending
  // mark a move sets, put a dropped card back where it started.
  const sourceTickets = searchActive ? (searchQ.data?.items ?? NO_TICKETS) : tickets.data;
  // The selection as the board knows it, for what the bulk Jira actions say
  // first (TOADO-849). A card the board no longer shows counts in no column.
  const selectedTickets = useMemo(() => {
    const known = new Map((sourceTickets ?? NO_TICKETS).map((ticket) => [ticket.id, ticket]));
    return [...bulkSelection].map((id) => known.get(id) ?? { id, columnId: '', jira: undefined });
  }, [sourceTickets, bulkSelection]);
  const linkedSelection = selectedTickets.filter((ticket) => !!ticket.jira?.issueKey).map((ticket) => ticket.id);
  const startBulkSend = () => {
    const ids = selectedTickets.map((ticket) => ticket.id);
    const ask = bigSendCheck(selectedTickets, columns.data ?? []);
    if (ask) setBigSendAsk({ ids, ...ask });
    else sendToJiraMutation.mutate(ids);
  };
  // The date filters read their times in the person's zone (TOADO-904), so a
  // change of zone filters again (Codex, round 3).
  const displayZone = displayTimeZone();
  const filteredTickets = useMemo(() => {
    if (!sourceTickets || !prefs) return NO_TICKETS;
    const filtered = applyFilters(sourceTickets, prefs, query, user?.id ?? null, searchActive);
    return peopleFilter.length === 0
      ? filtered
      : filtered.filter((t) => t.assignees.some((a) => peopleFilter.includes(a)));
  }, [sourceTickets, prefs, query, user?.id, searchActive, peopleFilter, displayZone]);

  if (!project.data || !columns.data || !tickets.data || !prefs) {
    return <div style={{ padding: 40, color: 'var(--text-tertiary)' }}>Loading board…</div>;
  }
  // The workspace the project is in, which is not always the one selected: a
  // link can open a project from another workspace you belong to, and the
  // breadcrumb named the selected one over it.
  const projectCompany = companies.find((c) => c.id === project.data.companyId) ?? currentCompany;
  // What Cards and Table name, and clear in place, when filters hide every ticket (TOADO-976).
  const viewFilters = {
    names: activeFilterNames({
      prefs, columns: columns.data, ticketTypes: ticketTypes.data, query, peopleCount: peopleFilter.length,
      typeFilterApplies: !searchActive,
    }),
    onClear: () => {
      patchPrefs(clearedFilterPrefs(prefs));
      setPeopleFilter([]);
      setQuery('');
    },
  };


  // Keyboard focus model continuation — see the matching section above.
  // Move the keyboard cursor in Cards / Table views. No-op on Board /
  // Single. Wraps at the ends. If no current focus, picks the first
  // (delta 1) or last (delta -1) ticket so the first j/k keystroke
  // does something predictable.
  //
  // This is a regular function (not a hook), so it can live below the
  // early-return guard without affecting hook ordering. The matching
  // useEffect that clears focus on ticket deletion lives ABOVE the
  // early return — see the comment block flagged in the hook section.
  function moveFocus(delta: number) {
    if (prefs?.viewMode !== 'cards' && prefs?.viewMode !== 'table') return;
    const list = filteredTickets;
    if (list.length === 0) return;
    const i = focusedTicketId ? list.findIndex((t) => t.id === focusedTicketId) : -1;
    let next: number;
    if (i < 0) next = delta > 0 ? 0 : list.length - 1;
    else next = (i + delta + list.length) % list.length;
    const target = list[next];
    if (target) setFocusedTicketId(target.id);
  }

  const stats = {
    total: tickets.data.length,
    aiActivity: tickets.data.filter((t) => t.hasAiActivity).length,
    partial: tickets.data.filter((t) => t.health !== 'complete').length,
  };

  const handleOpenTicket = (t: TicketSummary) => openTicketOverBoard(t.id);
  const handleCopyLink = async (t: TicketSummary) => {
    await copyToClipboard(`${window.location.origin}/tickets/${t.id}`);
    toast.push({ variant: 'success', title: 'Link copied', description: 'Direct ticket URL is on your clipboard.' });
  };
  const handleOpenSource = (t: TicketSummary) => {
    safeOpen(t.sourceUrl);
  };
  const handleStatusClick = (t: TicketSummary, a: HTMLElement) =>
    setStatusTicket({ t, anchor: a.getBoundingClientRect() });
  // Only where the project has types to offer; otherwise a card's type pill just shows its type.
  const handleTypeClick = (ticketTypes.data?.length ?? 0) > 0
    ? (t: TicketSummary, a: HTMLElement) => setTypeTicket({ t, anchor: a.getBoundingClientRect() })
    : undefined;
  const handleOverflowClick = (t: TicketSummary, a: HTMLElement) =>
    setOverflowTicket({ t, anchor: a.getBoundingClientRect() });
  const handleAssignClick = (t: TicketSummary, a: HTMLElement) =>
    setAssignTicket({ t, anchor: a.getBoundingClientRect() });

  const teamAvatars = members.data?.map((m) => m.user) ?? [];

  const commitUrl = async () => {
    const val = urlDraft.trim();
    // Persist through the API — the previous implementation mutated the
    // React Query cache object in place and never saved, so the pattern
    // vanished on refresh (TOADO-98). The header edits slot [0] only;
    // preserve any additional patterns managed via Settings → URL
    // Patterns. An emptied field clears slot [0].
    const rest = project.data!.urlPatterns.slice(1);
    const newPatterns = val ? [val, ...rest] : rest;
    try {
      await api.updateProject(project.data!.id, { urlPatterns: newPatterns });
      await qc.invalidateQueries({ queryKey: ['project', project.data!.id] });
      readIconSoon(qc, project.data!.id, project.data!.companyId);
      setEditingUrl(false);
    } catch (e) {
      // Keep the editor open so the user's input isn't lost.
      toast.push({ variant: 'danger', title: 'Could not save URL pattern', description: (e as Error).message });
    }
  };
  const startEditUrl = () => {
    setUrlDraft(project.data!.urlPatterns[0] ?? '');
    setEditingUrl(true);
    // After the modal has mounted its field. The timeout predates the modal
    // and did the same job for an inline input; it still has to outlast the
    // render, so it stays.
    setTimeout(() => urlInputRef.current?.focus(), 10);
  };

  return (
    // Screens, for pins (TOADO-701): each view is one, at this one address.
    // The header and the toolbar are the same on all of them.
    <div className={styles.page}>
      <div className={styles.header} data-toado-view={EVERY_SCREEN}>
        <div className={styles.breadcrumbRow}>
          <div className={styles.breadcrumb}>
            {projectCompany && (
              <>
                {/* Home is the selected workspace's, so another workspace's name is not a link to it. */}
                {projectCompany.id === currentCompanyId
                  ? <a href="/">{projectCompany.name}</a>
                  : <span>{projectCompany.name}</span>}
                <span className={styles.sep}>/</span>
              </>
            )}
            <span style={{ color: 'var(--text)' }}>{project.data.name}</span>
            <span className={styles.sep}>/</span>
            <span>{viewModeLabel(prefs.viewMode)}</span>
          </div>
          <div className={styles.breadcrumbRight}>
            <div className={styles.stats}>
              <span><strong>{stats.total}</strong> tickets</span>
              <span className={styles.statSep} />
              <span><strong>{stats.aiActivity}</strong> with AI</span>
              <span className={styles.statSep} />
              <span><strong>{stats.partial}</strong> partial</span>
            </div>
            <div className={styles.teamStack} role="group" aria-label="Show tickets assigned to">
              {teamAvatars.slice(0, 5).map((u) => {
                const on = peopleFilter.includes(u.id);
                return (
                  <Tooltip key={u.id} content={on ? `${u.name}: showing their tickets` : `${u.name}: show their tickets`} placement="bottom">
                    <button
                      type="button"
                      className={cn(styles.teamAvatar, styles.teamAvatarBtn, on && styles.teamAvatarOn)}
                      aria-pressed={on}
                      aria-label={`Tickets assigned to ${u.name}`}
                      onClick={() => togglePerson(u.id)}
                    >
                      {/* No native title: the tooltip above says it (TOADO-800). */}
                      <Avatar name={u.name} size="sm" title={undefined} />
                    </button>
                  </Tooltip>
                );
              })}
              {teamAvatars.length > 5 && (
                <span className={styles.teamMore}>+{teamAvatars.length - 5}</span>
              )}
            </div>
          </div>
        </div>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>
            <ProjectMark project={project.data} size={22} />
            <InlineProjectName
              projectId={project.data.id}
              companyId={project.data.companyId}
              name={project.data.name}
            />
            {/*
              * The pattern opens the thing that explains it.
              *
              * This was a bare input with a placeholder, in a header row with
              * no space to say what a pattern means - and the syntax is not
              * guessable: `*` is one path segment, so the obvious `/*` routes
              * the top of a site and silently misses everything deeper. The
              * badge is now the control, and it opens an editor that carries
              * the same explanation the settings tab gives, plus the way
              * through to the rest of the patterns this project has.
              */}
            <div className={styles.urlCell}>
              {project.data.urlPatterns[0] ? (
                <Tooltip content="Edit URL pattern">
                  <button
                    type="button"
                    className={styles.urlBadgeBtn}
                    onClick={startEditUrl}
                    aria-label={`Edit URL pattern ${project.data.urlPatterns[0]}`}
                  >
                    <Badge variant="outline" className={styles.urlBadge}>{project.data.urlPatterns[0]}</Badge>
                    <Pencil size={12} className={styles.urlBadgePencil} />
                  </button>
                </Tooltip>
              ) : (
                <button className={styles.addUrlBtn} onClick={startEditUrl}>
                  <Pencil size={12} /> Add URL pattern
                </button>
              )}
            </div>
          </h1>
          <div className={styles.titleRight}>
            <div className={styles.subnavSearch}>
              <SearchIcon size={14} className={styles.subnavSearchIcon} />
              <input
                ref={searchInputRef}
                className={styles.subnavSearchInput}
                data-tour-anchor="board.search"
                placeholder="Search tickets…    /"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    // Claimed when it cleared the search, so it closes nothing else
                    // (an onboarding tour); an empty search leaves the key free.
                    if (query) e.preventDefault();
                    setQuery('');
                    e.currentTarget.blur();
                  }
                }}
              />
              {query && (
                <button className={styles.subnavSearchClear} onClick={() => setQuery('')} aria-label="Clear search">×</button>
              )}
              <Tooltip content="Advanced search" placement="bottom">
                <button
                  type="button"
                  className={styles.subnavSearchAdv}
                  onClick={() => setAdvancedSearchOpen(true)}
                  aria-label="Advanced search"
                >
                  <SlidersHorizontal size={14} />
                </button>
              </Tooltip>
              <Tooltip content="Keyboard shortcuts (?)" placement="bottom">
                <button
                  type="button"
                  className={styles.subnavSearchAdv}
                  onClick={() => setShortcutsOpen(true)}
                  aria-label="Keyboard shortcuts"
                >
                  <Keyboard size={14} />
                </button>
              </Tooltip>
            </div>
            <Tooltip content="Drafts" placement="bottom">
              <button
                className={styles.iconBtn}
                onClick={() => navigate(`/projects/${projectId}/drafts`)}
                aria-label="Drafts"
              >
                <DraftsIcon size={16} />
              </button>
            </Tooltip>
            <Tooltip content="Archive" placement="bottom">
              <button
                className={styles.iconBtn}
                onClick={() => navigate(`/projects/${projectId}/archive`)}
                aria-label="Archive"
              >
                <ArchiveIcon size={16} />
              </button>
            </Tooltip>
            <Tooltip content="Project settings" placement="bottom">
              <button
                className={styles.iconBtn}
                onClick={() => navigate(`/projects/${projectId}/settings`)}
                aria-label="Settings"
                data-tour-anchor="board.settings"
              >
                <SettingsIcon size={16} />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      <div className={styles.navToolbar} data-toado-view={EVERY_SCREEN}>
        <div className={styles.navLeft}>
          <BoardToolbarWithView
            prefs={prefs}
            patch={patchPrefs}
            onCreateCapture={() => setCreateOpen(true)}
            columns={columns.data}
            ticketTypes={ticketTypes.data}
            toolbarRef={toolbarRow}
          />
          <BulkActionBar
            ref={bulkBar}
            count={bulkSelection.size}
            onMove={() => setBulkMoveOpen(true)}
            onArchive={() => bulkArchiveMutation.mutate(Array.from(bulkSelection))}
            onAssign={(anchor) => setBulkAssignAnchor(anchor)}
            onType={(ticketTypes.data?.length ?? 0) > 0 ? (anchor) => setBulkTypeAnchor(anchor) : undefined}
            onSendToJira={canSendToJira ? startBulkSend : undefined}
            onUnlinkFromJira={canUnlinkFromJira && linkedSelection.length > 0 ? () => setUnlinkAsk(linkedSelection) : undefined}
            onClear={clearBulk}
            archiving={bulkArchiveMutation.isPending}
            moving={bulkMoveMutation.isPending}
            assigning={bulkAssignMutation.isPending}
            typing={bulkTypeMutation.isPending}
            sendingToJira={sendToJiraMutation.isPending}
            unlinkingFromJira={bulkUnlinkMutation.isPending}
          />
        </div>
      </div>

      <div
        className={cn(styles.viewArea, prefs.viewMode !== 'cards' && prefs.viewMode !== 'table' && styles.viewAreaFixed)}
        data-toado-view={`${PROJECT_BOARD_SCREEN}/${prefs.viewMode}`}
      >
        {prefs.viewMode === 'board' && (
          <BoardView
            columns={columns.data}
            tickets={filteredTickets}
            creatorsById={creatorsById}
            membersById={creatorsById}
            showThumbnails={prefs.thumbnails}
            onOpenTicket={handleOpenTicket}
            onCopyLink={handleCopyLink}
            onOpenSource={handleOpenSource}
            onMoveTicket={(ticketId, columnId, anchor) => moveMutation.mutate({ ticketId, columnId, anchor })}
            onBulkMove={(ticketIds, columnId) => startBulkMove(ticketIds, columnId)}
            onStatusClick={handleStatusClick}
            onTypeClick={handleTypeClick}
            onOverflowClick={handleOverflowClick}
            onAssignClick={handleAssignClick}
            onAddTicket={handleAddTicket}
            onColumnMenu={handleColumnMenu}
            bulkSelection={bulkSelection}
            onBulkToggle={toggleBulk}
            movingTicketIds={movingTicketIds}
            intakeEnabled={project.data?.bugIntakeEnabled ?? false}
          />
        )}
        {prefs.viewMode === 'cards' && (
          <CardsView
            tickets={filteredTickets}
            columns={columns.data}
            creatorsById={creatorsById}
            membersById={creatorsById}
            showThumbnails={prefs.thumbnails}
            onOpenTicket={handleOpenTicket}
            onCopyLink={handleCopyLink}
            onOpenSource={handleOpenSource}
            onStatusClick={handleStatusClick}
            onTypeClick={handleTypeClick}
            onOverflowClick={handleOverflowClick}
            onAssignClick={handleAssignClick}
            bulkSelection={bulkSelection}
            onBulkToggle={toggleBulk}
            movingTicketIds={movingTicketIds}
            focusedTicketId={focusedTicketId}
            onFocus={setFocusedTicketId}
            filters={viewFilters}
          />
        )}
        {prefs.viewMode === 'table' && (
          <TableView
            tickets={filteredTickets}
            columns={columns.data}
            creatorsById={creatorsById}
            membersById={creatorsById}
            onOpenTicket={handleOpenTicket}
            onCopyLink={handleCopyLink}
            onOpenSource={handleOpenSource}
            onStatusClick={handleStatusClick}
            onOverflowClick={handleOverflowClick}
            onAssignClick={handleAssignClick}
            bulkSelection={bulkSelection}
            onBulkToggle={toggleBulk}
            movingTicketIds={movingTicketIds}
            focusedTicketId={focusedTicketId}
            onFocus={setFocusedTicketId}
            filters={viewFilters}
          />
        )}
        {prefs.viewMode === 'single' && (
          <SingleView
            tickets={filteredTickets}
            columns={columns.data}
            creatorsById={creatorsById}
            membersById={creatorsById}
            movingTicketIds={movingTicketIds}
            onStatusClick={handleStatusClick}
            onTypeClick={handleTypeClick}
            onOpenSource={handleOpenSource}
            onCopyLink={handleCopyLink}
            onOverflowClick={handleOverflowClick}
            onAssignClick={handleAssignClick}
            onClose={() => patchPrefs({ viewMode: 'board' })}
          />
        )}
      </div>

      <Popover
        open={!!statusTicket}
        onClose={() => setStatusTicket(null)}
        anchor={statusTicket?.anchor ?? null}
        placement="bottom-start"
      >
        <Menu>
          {columns.data.map((c) => (
            <MenuItem
              key={c.id}
              className={cn(c.id === statusTicket?.t.columnId && styles.menuItemCurrent)}
              onClick={() => {
                if (statusTicket) moveMutation.mutate({ ticketId: statusTicket.t.id, columnId: c.id });
                setStatusTicket(null);
              }}
              leftIcon={<ColumnDot tone={c.tone} />}
            >
              {c.name}
            </MenuItem>
          ))}
        </Menu>
      </Popover>

      {/* A card's type pill: the types this project offers, as the bulk picker
          lists them, plus the card's own type if the project has since hidden
          it, so the menu still shows which one it is. */}
      <Popover
        open={!!typeTicket}
        onClose={() => setTypeTicket(null)}
        anchor={typeTicket?.anchor ?? null}
        placement="bottom-start"
      >
        <Menu>
          {(ticketTypes.data ?? [])
            .filter((type) => !type.hidden || type.id === typeTicket?.t.type?.id)
            .map((type) => (
              <MenuItem
                key={type.id}
                className={cn(type.id === typeTicket?.t.type?.id && styles.menuItemCurrent)}
                onClick={() => {
                  if (typeTicket) {
                    retypeTicket(typeTicket.t, {
                      id: type.id, name: type.name, glyph: type.glyph,
                      ...(type.companyName ? { companyName: type.companyName } : {}),
                    });
                  }
                }}
              >
                {type.name}
              </MenuItem>
            ))}
          <MenuDivider />
          <MenuItem
            className={cn(typeTicket && !typeTicket.t.type && styles.menuItemCurrent)}
            onClick={() => { if (typeTicket) retypeTicket(typeTicket.t, null); }}
          >
            No type
          </MenuItem>
        </Menu>
      </Popover>

      <Popover
        open={!!overflowTicket}
        onClose={() => setOverflowTicket(null)}
        anchor={overflowTicket?.anchor ?? null}
        placement="bottom-end"
      >
        <Menu>
          <MenuItem onClick={() => { if (overflowTicket) handleOpenTicket(overflowTicket.t); setOverflowTicket(null); }}>
            Open ticket
          </MenuItem>
          {/* Open ticket opens it over the board; this is its own page (Jason, 18 Oct). */}
          <MenuItem onClick={() => { if (overflowTicket) navigate(`/tickets/${overflowTicket.t.id}`); setOverflowTicket(null); }}>
            Open full page
          </MenuItem>
          <MenuItem onClick={() => { if (overflowTicket) handleCopyLink(overflowTicket.t); setOverflowTicket(null); }}>
            Copy link
          </MenuItem>
          {capturedPageUrl(overflowTicket?.t.sourceUrl) && (
            <MenuItem
              onClick={() => {
                const url = capturedPageUrl(overflowTicket?.t.sourceUrl);
                if (url) window.open(url, '_blank', 'noopener,noreferrer');
                setOverflowTicket(null);
              }}
            >
              Open the captured page
            </MenuItem>
          )}
          {overflowTicket?.t.branchName && (
            <MenuItem
              onClick={async () => {
                if (!overflowTicket?.t.branchName) return;
                await copyToClipboard(overflowTicket.t.branchName);
                toast.push({ variant: 'success', title: 'Branch name copied' });
                setOverflowTicket(null);
              }}
            >
              {`Branch: ${overflowTicket.t.branchName}`}
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              if (!overflowTicket) return;
              setBranchTicketTarget(overflowTicket.t);
              setOverflowTicket(null);
              setBranchOpen(true);
            }}
          >
            {overflowTicket?.t.branchName ? 'Edit branch name…' : 'Set branch name…'}
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (!overflowTicket) return;
              setMoveTicketTarget(overflowTicket.t);
              setOverflowTicket(null);
              setMoveOpen(true);
            }}
          >
            Move to project…
          </MenuItem>
          {/* A board's sample ticket is never sent to Jira (TOADO-988). */}
          {overflowTicket && <JiraMenuItem jira={overflowTicket.t.jira} canSend={canSendToJira && !overflowTicket.t.isSample} canUnlink={canUnlinkFromJira}
            onSend={() => sendToJiraMutation.mutate([overflowTicket.t.id])}
            onUnlink={() => unlinkFromJiraMutation.mutate(overflowTicket.t)} onDone={() => setOverflowTicket(null)} />}
          <MenuDivider />
          <MenuItem
            onClick={() => {
              if (!overflowTicket) return;
              archiveMutation.mutate(overflowTicket.t);
              setOverflowTicket(null);
            }}
          >
            Archive
          </MenuItem>
          <MenuItem
            destructive
            onClick={() => {
              if (!overflowTicket) return;
              setDeleteTicketTarget(overflowTicket.t);
              setOverflowTicket(null);
              setDeleteOpen(true);
            }}
          >
            Delete…
          </MenuItem>
        </Menu>
      </Popover>

      {/* Column header three-dot menu. Keeps actions minimal — rename and
          add-ticket are quick; full management lives in settings. */}
      <Popover
        open={!!columnMenu}
        onClose={() => setColumnMenu(null)}
        anchor={columnMenu?.anchor ?? null}
        placement="bottom-end"
      >
        <Menu>
          <MenuItem
            onClick={() => {
              if (!columnMenu) return;
              handleAddTicket(columnMenu.columnId);
              setColumnMenu(null);
            }}
          >
            Add ticket
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (!columnMenu) return;
              handleRenameColumn(columnMenu.columnId);
              setColumnMenu(null);
            }}
          >
            Rename column…
          </MenuItem>
          <MenuDivider />
          <MenuItem
            onClick={() => {
              setColumnMenu(null);
              // Straight to the Columns section, not General (board ticket t-3_j3Pw).
              navigate(`/projects/${projectId}/settings?tab=columns`);
            }}
          >
            Manage columns…
          </MenuItem>
        </Menu>
      </Popover>

      {/* Inline "Add ticket" popover — a single title input, Enter submits. */}
      <Popover
        open={!!addTicket}
        onClose={() => { setAddTicket(null); setAddDraft(''); }}
        anchor={addTicket?.anchor ?? null}
        placement="top-start"
        width={260}
      >
        <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Input
            autoFocus
            value={addDraft}
            onChange={(e) => setAddDraft(e.target.value)}
            placeholder="Ticket title"
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); submitAddTicket(); }
              if (e.key === 'Escape') { setAddTicket(null); setAddDraft(''); }
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setAddTicket(null); setAddDraft(''); }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={submitAddTicket}
              disabled={!addDraft.trim() || createTicketMutation.isPending}
              loading={createTicketMutation.isPending}
            >
              Add
            </Button>
          </div>
        </div>
      </Popover>

      {/* Assignee picker */}
      <Popover
        open={!!assignTicket}
        onClose={() => setAssignTicket(null)}
        anchor={assignTicket?.anchor ?? null}
        placement="bottom-end"
        width={280}
      >
        {assignTicket && (
          <AssigneePicker
            allMembers={teamAvatars}
            selected={assignTicket.t.assignees}
            onDone={(userIds) => {
              assignMutation.mutate({ ticketId: assignTicket.t.id, userIds });
              setAssignTicket(null);
            }}
            onCancel={() => setAssignTicket(null)}
          />
        )}
      </Popover>

      {/* Bulk assignee picker — opens from the BulkActionBar's "Assign…"
          button. Empty selected[] (each checked ticket may have a
          different assignee set, so there's no single "current" to
          show); the chosen mode (replace / add / remove) decides how
          the picked user ids fan out across each ticket. */}
      <Popover
        open={!!bulkAssignAnchor}
        onClose={() => setBulkAssignAnchor(null)}
        anchor={bulkAssignAnchor}
        placement="bottom-end"
        width={300}
      >
        {bulkAssignAnchor && (
          <AssigneePicker
            allMembers={teamAvatars}
            selected={[]}
            bulkMode
            onDone={(userIds, mode) => {
              bulkAssignMutation.mutate({
                ticketIds: Array.from(bulkSelection),
                userIds,
                mode,
              });
            }}
            onCancel={() => setBulkAssignAnchor(null)}
          />
        )}
      </Popover>

      {/* Bulk type picker, from the BulkActionBar's "Type..." button. Only the
          types this project offers, as a new ticket's picker shows, plus a way
          to clear the type. */}
      <Popover
        open={!!bulkTypeAnchor}
        onClose={() => setBulkTypeAnchor(null)}
        anchor={bulkTypeAnchor}
        placement="bottom-end"
      >
        <Menu>
          {(ticketTypes.data ?? []).filter((type) => !type.hidden).map((type) => (
            <MenuItem
              key={type.id}
              onClick={() => retypeSelection(type.id, type.name)}
            >
              {type.name}
            </MenuItem>
          ))}
          <MenuDivider />
          <MenuItem
            onClick={() => retypeSelection(null, 'No type')}
          >
            No type
          </MenuItem>
        </Menu>
      </Popover>

      {branchTicketTarget && (
        <BranchNameModal
          open={branchOpen}
          onClose={() => { setBranchOpen(false); setBranchTicketTarget(null); }}
          ticketId={branchTicketTarget.id}
          projectId={branchTicketTarget.projectId}
          currentBranch={branchTicketTarget.branchName}
        />
      )}

      <MoveToProjectModal
        open={moveOpen}
        onClose={() => { setMoveOpen(false); setMoveTicketTarget(null); }}
        currentProjectId={projectId ?? ''}
        onMove={(targetProjectId, targetColumnId) => {
          if (moveTicketTarget) {
            moveMutation.mutate({
              ticketId: moveTicketTarget.id,
              columnId: targetColumnId,
              targetProjectId,
            });
            toast.push({
              variant: 'success',
              title: 'Ticket moved',
              description: moveTicketTarget.title.slice(0, 60),
            });
          }
          setMoveOpen(false);
          setMoveTicketTarget(null);
        }}
      />

      {/* Bulk-move modal — same UI as the single-ticket one but the onMove
          fans out to every id in the bulk selection. */}
      <MoveToProjectModal
        open={bulkMoveOpen}
        onClose={() => setBulkMoveOpen(false)}
        currentProjectId={projectId ?? ''}
        onMove={(targetProjectId, targetColumnId) => {
          const count = bulkSelection.size;
          if (bulkMoveMutation.isPending) return;
          startBulkMove(Array.from(bulkSelection), targetColumnId, targetProjectId);
          // Immediate "kicked off" toast — the success toast lands later
          // when the awaited refetch settles. Tells the user the request
          // is in flight (and they can navigate away if they want, since
          // the mutation runs against the React Query cache and survives
          // route changes).
          if (count > 1) {
            toast.push({
              variant: 'info',
              title: `Moving ${count} tickets…`,
              description: 'You can keep working. We\'ll let you know when it\'s done.',
            });
          }
        }}
      />

      <DestructiveConfirm
        open={!!unlinkAsk}
        onClose={() => setUnlinkAsk(null)}
        onConfirm={() => { if (unlinkAsk) bulkUnlinkMutation.mutate(unlinkAsk); }}
        title={`Unlink ${unlinkAsk?.length ?? 0} ${unlinkAsk?.length === 1 ? 'ticket' : 'tickets'} from Jira?`}
        description="Toado forgets each card's Jira issue. Jira is not changed: its issues stay as they are."
        confirmLabel="Unlink"
      />
      <Modal open={!!bigSendAsk} onClose={() => setBigSendAsk(null)} width={460}>
        <ModalHeader>{bigSendAsk?.title}</ModalHeader>
        <ModalBody><p style={{ margin: 0 }}>{bigSendAsk?.description}</p></ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setBigSendAsk(null)}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => { if (bigSendAsk) sendToJiraMutation.mutate(bigSendAsk.ids); setBigSendAsk(null); }}
          >
            Send {bigSendAsk?.ids.length}
          </Button>
        </ModalFooter>
      </Modal>
      <DestructiveConfirm
        open={deleteOpen}
        onClose={() => { setDeleteOpen(false); setDeleteTicketTarget(null); }}
        onConfirm={async () => {
          if (!deleteTicketTarget) return;
          const target = deleteTicketTarget;
          markPending(target.id);
          try {
            await api.deleteTicket(target.id);
            qc.invalidateQueries({ queryKey: ['tickets', projectId] });
            qc.invalidateQueries({ queryKey: ['tickets', projectId, 'archived'] });
            setDeleteOpen(false);
            setDeleteTicketTarget(null);
            toast.push({
              variant: 'info',
              title: 'Deleted',
              description: `${target.title.slice(0, 60)} (recoverable from Archive for 30 days)`,
              action: {
                label: 'Undo',
                onClick: () => {
                  api.restoreTicket(target.id).then(() => {
                    qc.invalidateQueries({ queryKey: ['tickets', projectId] });
                    qc.invalidateQueries({ queryKey: ['tickets', projectId, 'archived'] });
                    toast.push({ variant: 'success', title: 'Ticket restored' });
                  }).catch((err) => {
                    toast.push({ variant: 'danger', title: 'Restore failed', description: (err as Error).message });
                  });
                },
              },
            });
          } catch (err) {
            toast.push({ variant: 'danger', title: 'Delete failed', description: (err as Error).message });
          } finally {
            unmarkPending(target.id);
          }
        }}
        title={`Delete "${deleteTicketTarget?.title.slice(0, 40) ?? 'ticket'}"?`}
        description="The ticket, capture, and annotations will be permanently removed in 30 days. You can restore it from Archive before then."
        confirmLabel="Delete"
        holdToConfirm
      />

      <CreateCaptureModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreateText={async (title) => { await createTicketMutation.mutateAsync({ title }); }}
        submitting={createTicketMutation.isPending}
      />

      <AdvancedSearchModal
        open={advancedSearchOpen}
        onClose={() => setAdvancedSearchOpen(false)}
        defaultProjectId={projectId}
      />

      <ShortcutsCheatsheet open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

      <Modal open={editingUrl} onClose={() => setEditingUrl(false)} width={520}>
        <ModalHeader>URL pattern</ModalHeader>
        <ModalBody>
          <p className={styles.urlModalIntro}>
            Captures from a page matching this pattern land in this project automatically.
          </p>
          <Input
            ref={urlInputRef}
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void commitUrl();
              if (e.key === 'Escape') setEditingUrl(false);
            }}
            placeholder="https://myapp.com"
            aria-label="URL pattern"
          />
          <div className={styles.urlModalHelp}>
            <UrlPatternHelp />
          </div>
          {/*
            * The header edits the first pattern only, which is fine until a
            * project has several - and then a person who only ever sees this
            * box has no idea the others exist. Say so, and link to them.
            */}
          <button
            type="button"
            className={styles.urlModalLink}
            onClick={() => navigate(`/projects/${projectId}/settings?tab=urls`)}
          >
            Manage all patterns for this project
            {project.data.urlPatterns.length > 1 ? ` (${project.data.urlPatterns.length} saved)` : ''}
          </button>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setEditingUrl(false)}>Cancel</Button>
          <Button variant="primary" onClick={() => void commitUrl()}>Save pattern</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

// Toolbar with the view toggle inline after "New ticket"
function BoardToolbarWithView({
  prefs, patch, onCreateCapture, columns, ticketTypes, toolbarRef,
}: {
  prefs: ProjectViewPrefs;
  patch: (p: Partial<ProjectViewPrefs>) => void;
  onCreateCapture?: () => void;
  columns?: import('@vd/types').Column[];
  ticketTypes?: import('@vd/types').ProjectTicketType[];
  /** The toolbar's row of controls, which the bulk bar can lie over. */
  toolbarRef: RefObject<HTMLDivElement | null>;
}) {
  return (
    <div className={styles.toolbarWithView}>
      <BoardToolbar
        ref={toolbarRef}
        prefs={prefs}
        patch={patch}
        onCreateCapture={onCreateCapture}
        columns={columns}
        ticketTypes={ticketTypes}
        viewToggle={<ViewToggle viewMode={prefs.viewMode} onChange={(v) => patch({ viewMode: v })} tourAnchor="board.views" />}
      />
    </div>
  );
}

function applyFilters(
  tickets: TicketSummary[],
  prefs: ProjectViewPrefs,
  query: string,
  currentUserId: string | null,
  /** True when the input `tickets` came from the server-side
   * /search/tickets call (the api already matched against title,
   * description, comment bodies, and source URL). When true we skip
   * the redundant client-side q-filter at the bottom of this function. */
  searchActive: boolean,
): TicketSummary[] {
  const { quickFilters, sort, assigneeIds, authorIds, columnIds, createdFrom, createdTo, viewMode } = prefs;
  const q = query.trim().toLowerCase();
  let out = tickets.filter((t) => !t.archivedAt || quickFilters.archived);
  if (quickFilters.hasAiActivity) out = out.filter((t) => t.hasAiActivity);
  if (quickFilters.capturePartial) out = out.filter((t) => t.health !== 'complete');
  if (quickFilters.unresolved) out = out.filter((t) => !t.resolved);
  // Treat a missing `source` as 'extension' — tickets that predate the
  // provenance columns all came from there.
  if (quickFilters.externalReports) {
    out = out.filter((t) => t.source === 'widget' || t.source === 'public_link');
  }
  if (quickFilters.mentionsMe && currentUserId) {
    out = out.filter((t) => (t.mentions ?? []).includes(currentUserId));
  }
  if (columnIds && columnIds.length > 0) {
    out = out.filter((t) => columnIds.includes(t.columnId));
  }
  if (assigneeIds && assigneeIds.length > 0) {
    out = out.filter((t) => t.assignees.some((a) => assigneeIds.includes(a)));
  }
  if (authorIds && authorIds.length > 0) {
    out = out.filter((t) => authorIds.includes(t.createdBy));
  }
  if (createdFrom) {
    const fromMs = wallTimeIn(createdFrom)?.getTime() ?? NaN;
    if (Number.isFinite(fromMs)) out = out.filter((t) => new Date(t.createdAt).getTime() >= fromMs);
  }
  if (createdTo) {
    const toMs = wallTimeEndIn(createdTo)?.getTime() ?? NaN;
    if (Number.isFinite(toMs)) out = out.filter((t) => new Date(t.createdAt).getTime() <= toMs);
  }
  if (searchActive) {
    // The input list IS the search result; api already matched
    // title/description/comments/url with proper escaping. Nothing to
    // re-filter on `q` here.
  } else if (q) {
    // Sub-2-char fallback: the api requires q ≥ 2, so for shorter
    // queries we just match on title + sourceUrl over loaded tickets.
    out = out.filter((t) => t.title.toLowerCase().includes(q) || t.sourceUrl.toLowerCase().includes(q));
  }
  // 'user-sorted' preserves the server's (columnId, position, createdAt)
  // ordering, which IS the user's drag order on the kanban. Any other
  // sort overrides drag-within-column on every render — drag-between-
  // columns still works (changes columnId) but drag-within-column
  // snaps back. The board view uses this returned order both for its
  // column-internal slices AND for the optimistic-drag local state.
  if (sort === 'user-sorted') return out;
  return [...out].sort((a, b) => {
    if (sort === 'newest') return b.createdAt.localeCompare(a.createdAt);
    if (sort === 'oldest') return a.createdAt.localeCompare(b.createdAt);
    if (sort === 'last-activity') {
      // updatedAt falls back to createdAt for tickets that were
      // captured but never edited (the common case for fresh tickets).
      const aAt = a.updatedAt ?? a.createdAt;
      const bAt = b.updatedAt ?? b.createdAt;
      return bAt.localeCompare(aAt);
    }
    if (sort === 'most-comments') return b.commentCount - a.commentCount;
    return 0;
  });
}

/** Human-readable label for the breadcrumb's "where you are" segment. */
function viewModeLabel(mode: ProjectViewPrefs['viewMode']): string {
  switch (mode) {
    case 'board': return 'Board';
    case 'cards': return 'Cards';
    case 'single': return 'Queue';
    case 'table': return 'Table';
    default: return 'Board';
  }
}
